import {APIRequest} from "../APIRequest.js";
import {getBalanceJWT} from "../Ledger";
import {signKBTransactionJWT} from "../../Crypto/utils";
import {MyServices} from "../../constants";
import {deepClone} from "../../App/utils";
import {encryptKBItem} from "../../App/cryptoUtils";
import {initDB} from "../../Engines/Dexie";
import {createEmbeddingItem} from "../OpenAIAPI";
import {toYAML} from "../../utils";
import {renderTemplate} from "../../Item/ItemHelpers";

export const ITEMS_API_URL = 'https://xsdyx4yan2lvvjumeu5v3ecpha0wdsgn.lambda-url.us-east-1.on.aws/';
// export const ITEMS_API_URL = 'https://items.openkbs.com/';

export const deletedItemsCache = {};

const generatePaymentTokens = async (KBData) => {
    const { accountId, walletPublicKey } = KBData;
    const walletPrivateKey = KBData?.walletPrivateKey;
    const myBalanceJWT = await getBalanceJWT(accountId, walletPrivateKey, walletPublicKey)

    const transactionJWT = await signKBTransactionJWT({
        toAccountId: MyServices?.nosql?.accountId,
        ...KBData
    })

    return {
        myBalanceJWT,
        transactionJWT
    }
}

export const deleteItems = async (itemIds, KBData) => {
    const token = localStorage.getItem('kbJWT');
    const {myBalanceJWT, transactionJWT} = await generatePaymentTokens(KBData);
    try {
        const response = await APIRequest('post', ITEMS_API_URL, {
            action: 'deleteItems',
            itemIds,
        },{
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        });

        for (let pid of itemIds) deletedItemsCache[pid] = 1;

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const deleteItem = async ({itemId, KBData, itemType}) => {
    const token = localStorage.getItem('kbJWT');
    const {myBalanceJWT, transactionJWT} = await generatePaymentTokens(KBData);
    try {
        const response = await APIRequest('post', ITEMS_API_URL, {
            action: 'deleteItem',
            itemId,
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        });

        if (KBData?.searchEngine === "IndexedDB" && itemType) {
            const db = await initDB({KBData})
            await db[itemType].delete(itemId)
        }

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

const itemEncryptFields = (item, attributes) => {
    const itemCopy = deepClone(item)

    Object.keys(itemCopy).forEach(attrName => {
        const fieldForEncryption = attributes.find(a => a.attrName === attrName && a.encrypted);
        if (fieldForEncryption?.attrName && itemCopy[fieldForEncryption.attrName]) {
            itemCopy[fieldForEncryption.attrName] = encryptKBItem(itemCopy[fieldForEncryption.attrName])
        }
    })

    return itemCopy
}

export const updateItem = async ({itemId, attributes, itemType, KBData, item}) => {
    const token = localStorage.getItem('kbJWT');
    const {myBalanceJWT, transactionJWT} = await generatePaymentTokens(KBData);

    const itemData = { itemId, ...item}

    let embeddingChunk = toYAML(itemData);
    const embeddingTemplate = KBData?.itemTypes?.[itemType]?.embeddingTemplate

    if (embeddingTemplate) {
        embeddingChunk = renderTemplate(embeddingTemplate, itemData);
    }

    let totalTokens, embeddings;
    if (KBData?.embeddingModel && KBData?.embeddingModel !== 'none' && embeddingChunk) {
        ({ totalTokens, embeddings } = await createEmbeddingItem(embeddingChunk, KBData));
    }

    try {
        const response = await APIRequest('post', ITEMS_API_URL, {
            action: 'updateItem',
            itemId,
            totalTokens,
            embeddings: embeddings ? embeddings.slice(0, parseInt(KBData?.embeddingDimension)) : undefined,
            embeddingModel: KBData?.embeddingModel,
            embeddingDimension: KBData?.embeddingDimension,
            attributes,
            itemType,
            item: itemEncryptFields(item, attributes)
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        });

        if (KBData?.searchEngine === "IndexedDB") {
            await initDB({KBData})
        }

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const createItems = async (KBData, items) => {
    const token = localStorage.getItem('kbJWT');
    const { myBalanceJWT, transactionJWT } = await generatePaymentTokens(KBData);

    try {
        const response = await APIRequest('post', ITEMS_API_URL, {
            action: 'createItems',
            items: items.map(item => itemEncryptFields(item, item.attributes))
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            },
            timeout: 15 * 60 * 1000
        });

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

export const createItem = async ({itemId, attributes, itemType, KBData, item}) => {
    const token = localStorage.getItem('kbJWT');
    const {myBalanceJWT, transactionJWT} = await generatePaymentTokens(KBData);

    const itemData = {itemId, ...item}

    let embeddingChunk = toYAML(itemData);
    const embeddingTemplate = KBData?.itemTypes?.[itemType]?.embeddingTemplate

    if (embeddingTemplate) {
        embeddingChunk = renderTemplate(embeddingTemplate, itemData);
    }

    let totalTokens, embeddings;
    if (KBData?.embeddingModel && KBData?.embeddingModel !== 'none' && embeddingChunk) {
        ({ totalTokens, embeddings } = await createEmbeddingItem(embeddingChunk, KBData));
    }

    try {
        const response = await APIRequest('post', ITEMS_API_URL, {
            action: 'createItem',
            itemId: itemId ? itemId.toString() : undefined,
            totalTokens,
            embeddings: embeddings ? embeddings.slice(0, parseInt(KBData?.embeddingDimension)) : undefined,
            embeddingModel: KBData?.embeddingModel,
            embeddingDimension: KBData?.embeddingDimension,
            attributes,
            itemType,
            item: itemEncryptFields(item, attributes)
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        });

        if (KBData?.searchEngine === "IndexedDB") {
            await initDB({KBData})
        }

        return response?.data;
    } catch (error) {
        console.error('API request error:', error);
        throw error;
    }
};

const ongoingRequests = {};

export const getItem = async ({ itemId, KBData }) => {
    if (!KBData?.walletPrivateKey) return console.log('key missing');

    const { myBalanceJWT, transactionJWT } = await generatePaymentTokens(KBData);
    const token = localStorage.getItem('kbJWT');
    const cacheKey = `getItem-${itemId}`;

    // Check if there's an ongoing request for the same itemId
    if (!ongoingRequests[cacheKey]) {
        // If not, create a new request and store the promise
        ongoingRequests[cacheKey] = APIRequest('post', ITEMS_API_URL, {
            action: 'getItem',
            token,
            itemId
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        }, { optimistic: true }).finally(() => {
            // Once the request is finished, remove it from the cache
            delete ongoingRequests[cacheKey];
        });
    }

    // Wait for the request to resolve and get the response
    const response = await ongoingRequests[cacheKey];
    return response?.data;
};

export const fetchItems = async (KBData, params) => {
    if (!KBData?.walletPrivateKey) return console.log('key missing');

    const { myBalanceJWT, transactionJWT } = await generatePaymentTokens(KBData);

    const token = localStorage.getItem('kbJWT');
    const cacheKey = `fetchItems-${JSON.stringify(params)}`;

    // Check if there's an ongoing request for the same parameters
    if (!ongoingRequests[cacheKey]) {
        // If not, create a new request and store the promise
        ongoingRequests[cacheKey] = APIRequest('post', ITEMS_API_URL, {
            action: 'fetchItems',
            filterMeta: (params?.filterMeta ? params.filterMeta : undefined),
            limit: (params?.limit ? params.limit : undefined),
            exclusiveStartKey: (params?.exclusiveStartKey ? params.exclusiveStartKey : undefined),
            from: (params?.from ? params.from : undefined),
            to: (params?.to ? params.to : undefined),
            // field: ( (params?.field && (params?.to || params?.from) ) ? params.field : undefined),
            field: ( params?.field  ? params.field : undefined),

            // limit: 1000
        }, {
            headers: {
                'kb-jwt': token,
                'transaction-jwt': transactionJWT,
                'balance-jwt': myBalanceJWT,
            }
        }).finally(() => {
            // Once the request is finished, remove it from the cache
            delete ongoingRequests[cacheKey];
        });
    }

    // Wait for the request to resolve and get the response
    const response = await ongoingRequests[cacheKey];

    if (!response) return null

    const {items, ...restData} = response?.data

    const itemsData = items.filter(o => !deletedItemsCache[o.itemId])

    return {
        items: itemsData,
        ...restData
    }
};
